import { Button, Modal as BootstrapModal } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useGetUser, useHasEnvironmentsPermission } from '../../common/Hooks';
import TextBox from '../controls/form/TextBox';
import Dropdown from '../controls/form/Dropdown';
import Utils from '../../utils/utils';
import Deployment from '../../services/deployment';
import User from '../../services/user';
import PubSub from '../../services/PubSub';
import { useCallback, useEffect, useState, useMemo } from 'react';
import Environment from '../../services/environment';
import { useTranslation } from 'react-i18next';
import Product from '../../services/product';
import GenericDropdown from '../controls/GenericDropdown';

const CreateDeployment = ({ domain, show, onCancel }) => {
    const [environmentList, setEnvironmentList] = useState([]);
    const [productList, setProductList] = useState([]);
    const [productVersionList, setProductVersionList] = useState([]);
    const [environmentsWithPermission, setEnvironmentsWithPermission] = useState([]);
    const [translate] = useTranslation();
    const [, setUser] = useGetUser();
    const [selectedProduct, setSelectedProduct] = useState('');

    const environmentsPermissions = useHasEnvironmentsPermission({
        domain: Utils.getDomain(),
        environments: environmentList,
        deployment: null,
        type: 'deployment'
    });

    const getAllEnvironments = useCallback(async (domain) => {
        const response = await Environment.getAllEnvironmentsInDomain(domain, -1);
        if (!response.error) {
            setEnvironmentList([...response.data]);
        }
    }, [setEnvironmentList]);

    const getAllProductVersions = useCallback(async () => {
        const response = await Product.getAllProductVersions(Utils.getDomain());

        if (!response.error) {
            setProductVersionList(response.data);
            setProductList(Object.keys(response.data).map((product, id) => ({ _id: product })));
        }
    }, []);

    useEffect(() => {
        if (environmentList && environmentList.length > 0) {
            setEnvironmentsWithPermission((environments) => {
                environments = [];
                environmentList.forEach((env) => {
                    if (Object.keys(environmentsPermissions).length > 0 && environmentsPermissions[env._id]?.create) {
                        environments.push(env);
                    }
                });

                return environments;
            });
        }
    }, [environmentList, environmentsPermissions]);

    useEffect(() => {
        if (show) {
            getAllEnvironments(domain);
            getAllProductVersions();
            setSelectedProduct('');
        }
    }, [show, domain, getAllEnvironments, getAllProductVersions]);

    const create = useCallback(async ({ name, environment, productVersion, }) => {
        if (environment && productVersion) {
            const response = await Deployment.createDeployment(environment, {
                name,
                environment,
                productVersion,
            });
            if (!response.error) {
                const updatedUser = await User.getUser();
                if (!updatedUser.error) {
                    setUser({
                        _id: updatedUser.data._id,
                        email: updatedUser.data.email,
                        appAdmin: updatedUser.data.appAdmin,
                        firstName: updatedUser.firstName,
                        lastName: updatedUser.lastName,
                        adminForDomains: updatedUser.data.adminForDomains,
                        uiPermissions: updatedUser.data.uiPermissions
                    });
                }
                PubSub.publish('refresh-environment-deployments', { newDeployment: response.data });
                onCancel();
            }
        }
    }, [onCancel, setUser]);

    const renderForm = useMemo(() => {
        return (
            <Formik
                initialValues={{
                    name: '',
                    environment: '',
                    productVersion: '',
                }}
                validationSchema={Yup.object({
                    name: Yup.string().min(2, translate('OTM_CHARACTERS_OR_MORE', { minCharacters: 2 })).required(' '),
                    productVersion: Yup.string().required(' '),
                    environment: Yup.string().required(' '),
                })}
                onSubmit={create}
                enableReinitialize
            >
                <Form>
                    <BootstrapModal.Header className='h5 text-secondary'>{translate('OTM_CREATE_DEPLOYMENT')}</BootstrapModal.Header>
                    <BootstrapModal.Body>
                        <GenericDropdown
                            options={productList}
                            valueKey='_id'
                            displayValueKey='name'
                            heading={translate('OTM_SELECT_PRODUCT')}
                            onSelect={(value) => {
                                setSelectedProduct(value);
                            }}
                            grow={true}
                        />
                        <div className='mt-3'>
                            <Dropdown
                                name='productVersion'
                                displayValueKey='productVersion'
                                options={selectedProduct ? productVersionList[selectedProduct] : []}
                                heading={translate('OTM_SELECT_PRODUCT_VERSION')}
                                grow={true}
                            />
                        </div>
                        <div className='mt-3'>
                            <Dropdown
                                name='environment'
                                options={environmentsWithPermission}
                                heading={translate('OTM_SELECT_AN_ENVIRONMENT')}
                                readOnly={!selectedProduct}
                                grow={true}
                            />
                        </div>
                        <TextBox disabled={!selectedProduct} label={translate('OTM_NAME')} type='text' name='name' css='mt-3' />
                    </BootstrapModal.Body>
                    <BootstrapModal.Footer>
                        <Button disabled={!selectedProduct} type='submit' className='btn-sm'>{translate('OTM_CREATE')}</Button>
                        <Button onClick={onCancel} variant='secondary' className='btn-sm'>{translate('OTM_CANCEL')}</Button>
                    </BootstrapModal.Footer>
                </Form>
            </Formik>
        )
    }, [create, environmentsWithPermission, onCancel, productList, productVersionList, translate, selectedProduct]);

    return (
        <BootstrapModal show={show} backdrop='static'>
            {renderForm}
        </BootstrapModal>
    );
};

export default CreateDeployment;